  import React, {
    useEffect,
    useRef,
    useState,
    useMemo, 
    useCallback,
  } from "react";
  import { useParams, useNavigate, useSearchParams } from "react-router-dom";
  import DOMPurify from "dompurify";
  import { toast } from "sonner";

  import {
    useGetCourseBySlugQuery,
    useGetCourseProgressQuery,
    useStartLessonExerciseMutation,
    useSaveExerciseAnswerMutation,
    useSubmitLessonExerciseMutation,
    useUpdateLessonCompletionMutation,
  } from "@/features/api/courseApi";

  import { Button } from "@/components/ui/button";
  import { CardFooter } from "@/components/ui/card";
  import { Skeleton } from "@/components/ui/skeleton";
  import { Progress } from "@/components/ui/progress";

  import {
    ArrowRight,
    PlayCircle,
    RotateCcw,
    ArrowLeft,
    ChevronDown,
  } from "lucide-react";

  /* ================= EXERCISES ================= */
  import MultipleChoiceExercise from "@/assets/AllCourses/Exercises/MultipleChoice";
  import FillInTheBlanksExercise from "@/assets/AllCourses/Exercises/FillInTheBlanks";
  import PictureFillInTheBlanksExercise from "@/assets/AllCourses/Exercises/PictureFillInTheBlanks";
  import MatchingExercise from "@/assets/AllCourses/Exercises/Matching";
  import PronunciationExercise from "@/assets/AllCourses/Exercises/Pronunciation";
  import ListeningExercise from "@/assets/AllCourses/Exercises/Listening";
  import DragAndDropExercise from "@/assets/AllCourses/Exercises/DragAndDrop";
  import CategorizingWordsExercise from "@/assets/AllCourses/Exercises/CategorizingWords";
  import CircleTheCorrectWordExercise from "@/assets/AllCourses/Exercises/CircleTheCorrectWord";
  import CompleteTheConversationExercise from "@/assets/AllCourses/Exercises/CompleteTheConversation";
  import CompleteTheSentencesExercise from "@/assets/AllCourses/Exercises/CompleteTheSentences";
  import CrosswordExercise from "@/assets/AllCourses/Exercises/Crossword";
  import MissingAlphabetsExercise from "@/assets/AllCourses/Exercises/MissingAlphabets";
  import SentenceJumblesExercise from "@/assets/AllCourses/Exercises/SentenceJumbles";
  import StressingSyllableWordsExercise from "@/assets/AllCourses/Exercises/StressingSyllableWords";
  import VocabularyBuildingExercise from "@/assets/AllCourses/Exercises/VocabularyBuilding";
  import WordFormationExercise from "@/assets/AllCourses/Exercises/WordFormation";
  import WordJumblesExercise from "@/assets/AllCourses/Exercises/WordJumbles";



  /* ================= CONSTANTS ================= */
  const SCROLL_OFFSET = 140;

  

  /* ================= HELPERS ================= */
  const exerciseComponents = {
    "multiple-choice": MultipleChoiceExercise,
    "fill-in-the-blanks": FillInTheBlanksExercise,
    "picture-fill-in-the-blanks": PictureFillInTheBlanksExercise,
    matching: MatchingExercise,
    pronunciation: PronunciationExercise,
    listening: ListeningExercise,
    "drag-and-drop": DragAndDropExercise,
    "categorizing-words": CategorizingWordsExercise,
    "circle-the-correct-word": CircleTheCorrectWordExercise,
    "complete-the-conversation": CompleteTheConversationExercise,
    "complete-the-sentences": CompleteTheSentencesExercise,
    crossword: CrosswordExercise,
    "missing-alphabets": MissingAlphabetsExercise,
    "sentence-jumbles": SentenceJumblesExercise,
    "stressing-syllable-words": StressingSyllableWordsExercise,
    "vocabulary-building": VocabularyBuildingExercise,
    "word-formation": WordFormationExercise,
    "word-jumbles": WordJumblesExercise,
    "exercise-section": () => null,
  };



  const getTitle = (ex, i) => ex?.title?.trim() || `Exercise ${i + 1}`;

  /* ================= COMPONENT ================= */
  export default function CourseLearningPage() {
    const { slug } = useParams();
const navigate = useNavigate();
const startTimeRef = useRef(Date.now());
const lastSavedAnswersRef = useRef({});
const initRef = useRef(false);

const [openAccordion, setOpenAccordion] = useState(true);

const [answers, setAnswers] = useState({});
const [attemptId, setAttemptId] = useState(null);

const [showResult, setShowResult] = useState(false);
const [score, setScore] = useState(null);
const [retryMode, setRetryMode] = useState(false);
const [isSubmitting, setIsSubmitting] = useState(false);
const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);

/* ================= API ================= */
const { data, isLoading } = useGetCourseBySlugQuery(slug, {
  skip: !slug,
});

const course = data?.course;
const PASS_PERCENT = Number(course?.passPercentage || 30);

const { data: progressData, isLoading: progressLoading } =
  useGetCourseProgressQuery(course?._id, {
    skip: !course?._id,
  });

const [startAttempt] = useStartLessonExerciseMutation();
const [saveAnswer] = useSaveExerciseAnswerMutation();
const [submitAttempt] = useSubmitLessonExerciseMutation();
const [updateLessonCompletion] = useUpdateLessonCompletionMutation();

/* ================= LESSONS ================= */
const sortedLessons = useMemo(() => {
  return [...(course?.lessons || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
}, [course]);

const [searchParams] = useSearchParams();
const lessonIdFromUrl = searchParams.get("lesson");

/* ================= CURRENT LESSON ================= */
const lesson = useMemo(() => {
  if (!course || !sortedLessons.length) return null;

  const fromUrl = sortedLessons.find(
    (l) => l._id?.toString() === lessonIdFromUrl
  );

  if (fromUrl) return fromUrl;

  const incomplete = sortedLessons.find((l) =>
    progressData?.lessonsProgress?.some(
      (lp) =>
        lp.lessonId?.toString() === l._id?.toString() &&
        !lp.completed
    )
  );

  if (incomplete) return incomplete;

  return sortedLessons[0];
}, [sortedLessons, lessonIdFromUrl, progressData, course]);

/* ================= EXERCISES ================= */
const allExercises = useMemo(() => {
  if (!sortedLessons.length) return [];

  const result = [];

  sortedLessons.forEach((lesson) => {
    (lesson.exercises || []).forEach((ex, index) => {
      if (ex.type !== "exercise-section") {
        result.push({
          ...ex,
          lessonId: lesson._id,
          lessonTitle: lesson.title,
          order: index 
        });
      }
    });
  });

  return result;
}, [sortedLessons]);

const totalQuestions = allExercises.length;

const answeredCount = allExercises.filter(
  (ex) => answers[ex._id]?.answer !== undefined
).length;

const progressValue =
  totalQuestions === 0
    ? 0
    : Math.round((answeredCount / totalQuestions) * 100);

const currentAnswered =
  answers[allExercises[currentExerciseIndex]?._id]?.answer !== undefined;


  
const retryLesson = async () => {
  try {
    setAnswers({});
    setShowResult(false);
    setRetryMode(false);
    setScore(null);
    setCurrentExerciseIndex(0);

    lastSavedAnswersRef.current = {};

    const res = await startAttempt({
      lessonId: lesson._id,
    }).unwrap();

    setAttemptId(res?.attemptId);

    startTimeRef.current = Date.now();
    initRef.current = true; 
  } catch {
    toast.error("Retry failed");
  }
};

/* ================= NAVIGATION ================= */
const continueLesson = () => {
  const nextIndex = currentExerciseIndex + 1;

  if (nextIndex >= allExercises.length) {
    setShowResult(false);
    return;
  }

  setCurrentExerciseIndex(nextIndex);
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const goBackExercise = () => {
  if (currentExerciseIndex === 0) return;

  setCurrentExerciseIndex((prev) => prev - 1);
  window.scrollTo({ top: 0, behavior: "smooth" });
};

/* ================= START ATTEMPT (FIXED SINGLE) ================= */
useEffect(() => {
  if (!lesson?._id) return;

  initRef.current = false;

  const init = async () => {
    try {
      const res = await startAttempt({
        lessonId: lesson._id,
      }).unwrap();

      setAttemptId(res?.attemptId);
      startTimeRef.current = Date.now();
    } catch (e) {
      console.error(e);
    }
  };

  init();
}, [lesson?._id]);

/* ================= INIT INDEX ================= */
useEffect(() => {
  if (!allExercises.length || initRef.current) return;

  initRef.current = true;
  setCurrentExerciseIndex(0);
}, [allExercises]);

/* ================= GROUP ================= */
const grouped = useMemo(() => {
  const groups = {};

  allExercises.forEach((ex, index) => {
    if (!groups[ex.lessonTitle]) {
      groups[ex.lessonTitle] = [];
    }

    groups[ex.lessonTitle].push({
      ...ex,
      index,
    });
  });

  return Object.entries(groups).map(([title, items]) => ({
    title,
    items,
  }));
}, [allExercises]);





/* ================= AUTO SAVE ================= */
useEffect(() => {
  if (!attemptId || showResult) return;

  const interval = setInterval(async () => {
    try {
      const changed = Object.entries(answers).filter(([id, val]) => {
if (
  !val ||
  val.answer === undefined ||
  val.answer === null ||
  (typeof val.answer === "string" && val.answer.trim() === "") ||
  (typeof val.answer === "object" && Object.keys(val.answer).length === 0)
) {
  return false;
}

  const prev = lastSavedAnswersRef.current[id];
  return JSON.stringify(prev) !== JSON.stringify(val);
});
      if (!changed.length) return;

      await Promise.all(
        changed.map(([id, val]) =>
         saveAnswer({
            attemptId,
            exerciseId: id,
            answer: val.answer, 
          })
        )
      );

      lastSavedAnswersRef.current = { ...answers };
    } catch (e) {
      console.error(e);
    }
  }, 5000);

  return () => clearInterval(interval);
}, [answers, attemptId, showResult]);

/* ================= COMPLETE ================= */
const handleComplete = useCallback((id, engineData) => {
  const answer = engineData?.answer;

  if (
    answer === undefined ||
    answer === null ||
    (typeof answer === "string" && answer.trim() === "") ||
    (typeof answer === "object" && Object.keys(answer).length === 0)
  ) {
    return;
  }

  setAnswers((prev) => ({
    ...prev,
    [id]: { answer },
  }));
}, []);

/* ================= SUBMIT ================= */
const submit = async () => {
  if (isSubmitting) return;

  const payload = allExercises
    .map((ex) => {
      const a = answers[ex._id];
      if (!a) return null;

      return {
        exerciseId: ex._id,
        answer: a.answer,
      };
    })
    .filter(Boolean);

  if (payload.length !== allExercises.length) {
    return toast.error("Complete all questions");
  }

  try {
    setIsSubmitting(true);

    const timeSpent = Math.floor(
      (Date.now() - startTimeRef.current) / 1000
    );

    const res = await submitAttempt({
      attemptId,
      courseId: course._id,
      answers: payload,
      timeSpent,
      submittedAt: new Date().toISOString(),
    }).unwrap();

    console.log("API RESPONSE:", res);

    // ✅ SAFE SCORE EXTRACTION (FINAL FIX)
    const scoreValue = Number(res?.scorePercent ?? 0);

    // ✅ PROTECT AGAINST BAD DATA
    if (isNaN(scoreValue)) {
      toast.error("Invalid score from server");
      return;
    }

    setScore(scoreValue);
    setShowResult(true);
    setRetryMode(scoreValue < PASS_PERCENT);

  } catch (e) {
    console.error(e);
    toast.error("Submit failed");
  } finally {
    setIsSubmitting(false);
  }
};

    /* ================= UI ================= */
    if (isLoading || progressLoading || !lesson) {
  return <Skeleton className="h-40 w-full" />;
}

    if (!course) return <div className="p-8 text-red-600">Course not found</div>;

    return (
      <div className="flex gap-6 p-6">

        {/* ================= TOC ================= */}
        <aside className="w-80 bg-white p-6 rounded-xl shadow sticky top-6 h-fit">

          <button
            onClick={() => setOpenAccordion((p) => !p)}
            className="flex justify-between w-full font-bold text-xl mb-4"
          >
            {course.title}
            <ChevronDown className={openAccordion ? "rotate-180" : ""} />
          </button>

        <Progress value={progressValue} className="mb-4" />

          {openAccordion && grouped?.length > 0 &&
              grouped.map((group, gi) => (
              <div key={gi} className="mb-4">
                <h4 className="text-sm font-bold mb-2 pr-2">
                  {group.title}
                </h4>

                {group.items.map((ex) => {
                const active = currentExerciseIndex === ex.index;

                return (
                  <button
                    key={ex.index}
                    onClick={() => {
                      setCurrentExerciseIndex(ex.index);
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }, 0);
                    }}
                    className={`w-full text-left px-4 py-2 rounded mb-1
                    ${
                      active
                        ? "bg-blue-100 text-blue-700 border-l-4 border-blue-600"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                  >
                    {getTitle(ex, ex.index)}
                  </button>
                );
              })}
              </div>
            ))}
        </aside>

        {/* ================= MAIN ================= */}
        <main className="flex-1">
  {(() => {
    const ex = allExercises[currentExerciseIndex];
    if (!ex) return null;

    const Comp = exerciseComponents[ex.type];
    if (!Comp) return null;

    const answerState = answers[ex._id];

    return (
      <section className="mb-14 relative">
         <div className="absolute top-0 right-0  bg-white/80 backdrop-blur">
    <img
      src="https://fetc.in/image/logo/Syzygy.png"
      alt="Syzygy"
        className="h-[11.5rem] object-contain opacity-90"
    />
  </div>

        <h3 className="text-xl font-bold mb-6">
          {getTitle(ex, currentExerciseIndex)}
        </h3>

        {ex.details && (
          <div
            className="mb-6"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(ex.details),
            }}
          />
        )}

        <div className="p-6 rounded border bg-white">
          <Comp
            exercise={ex}
            showResult={showResult}
            userAnswer={answerState?.answer}
            isCorrect={answerState?.correct}
            onComplete={(engineData) =>
              handleComplete(ex._id, engineData)
            }
          />
        </div>
      </section>
    );
  })()}
<CardFooter className="mt-8 border-t pt-6">
  <div className="w-full flex items-center justify-between gap-4">

    {/* LEFT: BACK BUTTON */}
    <div>
      <Button
        onClick={goBackExercise}
        disabled={currentExerciseIndex === 0}
        className="min-w-[110px] justify-center"
        variant="outline"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
    </div>

    {/* RIGHT: ACTIONS */}
    <div className="flex flex-col items-end gap-2">

      {/* BUTTON ROW */}
      <div className="flex items-center gap-3">

        {/* NORMAL FLOW */}
        {!showResult && (
          <>
            {currentExerciseIndex < totalQuestions - 1 ? (
              <Button
                onClick={continueLesson}
                disabled={!currentAnswered}
                className="min-w-[110px] justify-center"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={
                  isSubmitting ||
                  answeredCount < totalQuestions
                }
                className="min-w-[110px] justify-center"
              >
                Submit
              </Button>
            )}
          </>
        )}

        {/* RESULT FLOW */}
        {showResult && (
          <>
            {retryMode && (
              <Button
                variant="destructive"
                onClick={retryLesson}
                className="min-w-[110px] justify-center"
              >
                Retry
              </Button>
            )}

            {retryMode && currentExerciseIndex < totalQuestions - 1 && (
              <Button
                onClick={continueLesson}
                className="min-w-[110px] justify-center"
              >
                Next
              </Button>
            )}

            {currentExerciseIndex === totalQuestions - 1 && (
              <Button
                onClick={() =>
                  navigate(`/dashboard/courses/${slug}/result`)
                }
                disabled={
                  !showResult ||
                  Number(score) < Number(PASS_PERCENT)
                }
                className="min-w-[110px] justify-center"
              >
                Continue
              </Button>
            )}
          </>
        )}
      </div>

      {/* WARNING TEXT */}
      {showResult && Number(score) < Number(PASS_PERCENT) && (
        <p className="text-sm text-red-500 text-right max-w-[220px]">
          You must score at least {PASS_PERCENT}% to continue
        </p>
      )}
    </div>
  </div>
</CardFooter>
</main>
      </div>
    );
  }
